import React, { useEffect } from 'react';
import useSeqConsult from './hooks/useSeqConsult';
import { Box, HStack, Text } from '@design-system/pc';
import styled, { css } from 'styled-components';
import SeqConsultQuestion from './SeqConsultQuestion';
import { SeqConsultProvider } from '@/providers/SeqConsultProvider';
import { FormProvider } from 'react-hook-form';
import SeqConsultControls from './SeqConsultControls';
import LoadImage from '@/components/LoadImage';
import {
  GTM_CSTT,
  GTM_CST_KEYWORD,
  GTM_CTA_KEY_NAME,
  GTM_CTA_REMO_EVENT_NAME,
  GTM_MATCH_CONSULT_KEYWORD,
  GTM_MATCH_CONSULT_VIEW_KEYNAME,
  GTM_SEQ_CONSULT_KEYWORD,
  GTM_SEQ_CONSULT_VIEW_KEYNAME,
} from '@/constants';
import { useDataLayer } from '@/hooks/useDataLayer';
import { useBraze } from '@/providers/BrazeProvider';
import { format } from 'date-fns';
import { HEADER_HEIGHT } from '@/styles';
import { getAsset } from '@/utils';

export interface SeqConsultProps {}

const SeqConsult = () => {
  const {
    hookForm,
    step,
    stepTitle,
    currentStepIndex,
    totalStepLength,
    handleChangeStep,
    handleNext,
    handlePrev,
    handleSubmit,
    userBasicInfo,
    defaultRadio,
    handleChangeIsDefaultAddress,
    stepBannerSrc,
    handleChangeTelCertificated,
    isTelCertificated,
    agentEmployeeNo,
    workAvailableAreas,
    isOpenWorkableBottomSheet,
    handleOpenBottomSheet,
    handleCloseBottomSheet,
    consultType,
  } = useSeqConsult();
  const { handlePushAndRemove } = useDataLayer();
  const { logCustomEvent } = useBraze();
  const isMatchType = consultType === 'MATCH';

  useEffect(() => {
    handlePushAndRemove({
      data: {
        event: GTM_CTA_REMO_EVENT_NAME,
        [GTM_CTA_KEY_NAME]: GTM_CSTT,
        [GTM_CST_KEYWORD]: isMatchType
          ? GTM_MATCH_CONSULT_KEYWORD
          : GTM_SEQ_CONSULT_KEYWORD,
        gtmKeyword: isMatchType
          ? GTM_MATCH_CONSULT_VIEW_KEYNAME
          : GTM_SEQ_CONSULT_VIEW_KEYNAME,
      },
      key: GTM_CTA_KEY_NAME,
    });

    logCustomEvent({
      eventName: 'bz_start_fastconsult',
      eventProperties: {
        bz_date: format(new Date(), 'yyyy-MM-dd'),
        bz_consultType: 'sequential',
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <SeqConsultProvider
      value={{
        step,
        currentStepIndex,
        consultType,
        handleChangeStep,
        handleNext,
        handlePrev,
        handleSubmit,
        userBasicInfo,
        defaultRadio,
        handleChangeIsDefaultAddress,
        handleChangeTelCertificated,
        isTelCertificated,
        agentEmployeeNo,
        workAvailableAreas,
        isOpenWorkableBottomSheet,
        handleOpenBottomSheet,
        handleCloseBottomSheet,
      }}
    >
      <FormProvider {...hookForm}>
        <Box as="form">
          <HStack space={112} alignment="start">
            <Box
              width={488}
              height={600}
              position="sticky"
              top={HEADER_HEIGHT + 60}
            >
              <LoadImage src={stepBannerSrc} />
            </Box>
            <Box width={570}>
              <Text variant="subTitle6" color="color_chip.gray.600">
                인테리어 상담신청
              </Text>

              <Box mb="28px">
                {/* 추가 배너 */}
                {step === 'personal' && (
                  <Box mt="16px">
                    <LoadImage
                      src={getAsset(
                        '/assets/images/seq-consult/personal/top_banner.png',
                      )}
                    />
                  </Box>
                )}

                {/* 메인 타이틀 */}
                {step !== 'personal' && (
                  <Text variant="headline2" color="color_chip.primary" mt="8px">
                    {stepTitle(step)}
                  </Text>
                )}

                {/* 그 외 추가 타이틀 */}
                {step === 'baseConstructionPlan.scopes' && (
                  <>
                    <Text
                      variant="body1_5"
                      color="color_chip.primary"
                      mt="8px"
                      mb="4px"
                    >
                      특정 공간 내 상품 부분교체만 원하시는 경우 &quot;기타
                      입력&quot;으로 신청해주세요.
                    </Text>
                    <Text variant="caption3" color="color_chip.gray.700">
                      예시) 싱크대 수전, 아일랜드장, 양변기, 도배/마루(거실만)
                      등
                    </Text>
                  </>
                )}

                {step === 'baseConstructionPlan.budget' && (
                  <>
                    <Text variant="body1_5" color="color_chip.primary" mt="8px">
                      예산 선택 시 더 정확한 상담이 가능해요. (상담 시 변경
                      가능)
                    </Text>
                  </>
                )}

                {step === 'baseConstructionPlan.desiredDay' && (
                  <>
                    <Text variant="body1_5" color="color_chip.primary" mt="8px">
                      (신청일 기준)
                    </Text>
                  </>
                )}
                {step === 'baseConstructionPlan.consultSchedule' && (
                  <>
                    <Text variant="body1_5" color="color_chip.primary" mt="8px">
                      디자이너와 직접 만나서 상담을 받으실 수 있는 일정을
                      선택해주세요.
                    </Text>
                  </>
                )}
              </Box>

              {/* 프로그레스 바 */}
              <HStack space={16} alignment="center" mb="48px">
                <ProgressBar now={currentStepIndex + 1} max={totalStepLength} />
                <HStack space={2}>
                  <Text variant="subTitle6" color="color_chip.primary">
                    {currentStepIndex + 1}
                  </Text>
                  <Text variant="subTitle6" color="color_chip.gray.600">
                    /
                  </Text>
                  <Text variant="subTitle6" color="color_chip.gray.600">
                    {totalStepLength}
                  </Text>
                </HStack>
              </HStack>

              <SeqConsultQuestion />
            </Box>
          </HStack>

          <SeqConsultControls />
        </Box>
      </FormProvider>
    </SeqConsultProvider>
  );
};

export default SeqConsult;

const ProgressBar = styled(Box)<{
  now: number;
  max: number;
}>`
  ${({ now, max, theme }) => css`
    position: relative;
    height: 2px;
    background-color: ${theme.colors.color_chip.gray[100]};
    content: ' ';
    overflow: hidden;
    display: ${max <= 1 ? 'none' : 'block'};
    flex-grow: 1;
    flex-basis: 1;
    flex-shrink: 0;
    &::after {
      position: absolute;
      top: 0;
      bottom: 0;
      left: 0;
      width: ${Math.ceil((1 / max) * 100) * now}%;
      height: 2px;
      background-color: ${theme.colors.color_chip.gray[700]};
      content: ' ';
      transition: all 0.3s ease;
    }
  `}
`;
